import { useState } from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  TooltipProps
} from 'recharts';
import { TrendingUp, BarChart3, Activity, Layers } from 'lucide-react';

// Define the data types
interface RevenueData {
  period: string;
  invoiced: number;
  collected: number;
  outstanding: number;
  invoice_count?: number;
  payment_count?: number;
}

interface FormattedRevenueData extends RevenueData {
  period_display: string;
}

interface RevenueChartProps {
  data?: RevenueData[];
  is_loading?: boolean;
}

const RevenueChart = ({ data = [], is_loading = false }: RevenueChartProps) => {
  const [chart_type, set_chart_type] = useState<'area' | 'line' | 'bar'>('bar');

  const format_currency = (value: number): string => {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    }
    if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}K`;
    }
    return value.toFixed(0);
  };

  const format_tooltip_currency = (value: number): string => {
    return `KES ${value?.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  const format_period = (period: string): string => {
    // Format period for better display
    if (period?.includes('W')) {
      // Week format: 2026-W02 -> Week 2, 2026
      const [year, week] = period.split('-W');
      return `Week ${week}, ${year}`;
    } else if (period?.length === 7) {
      // Month format: 2026-01 -> Jan 2026
      const [year, month] = period.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1);
      return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    }
    // Day format: 2026-01-10 -> Jan 10
    const date = new Date(period);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const custom_tooltip = ({ active, payload, label }: TooltipProps<number, string>) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-4 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold text-gray-800 mb-3 border-b pb-2">
            {format_period(label as string)}
          </p>
          <div className="space-y-2">
            {payload.map((entry, index) => (
              <div key={index} className="flex items-center justify-between gap-6 text-sm">
                <span className="flex items-center gap-2">
                  <span
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: entry.color }}
                  />
                  <span className="text-gray-600">{entry.name}:</span>
                </span>
                <span className="font-semibold" style={{ color: entry.color }}>
                  {format_tooltip_currency(entry.value as number)}
                </span>
              </div>
            ))}
          </div>
          {/* Additional info */}
          <div className="mt-3 pt-3 border-t space-y-1 text-xs text-gray-500">
            <div className="flex justify-between">
              <span>Invoices:</span>
              <span>{(payload[0]?.payload as RevenueData)?.invoice_count || 0}</span>
            </div>
            <div className="flex justify-between">
              <span>Payments:</span>
              <span>{(payload[0]?.payload as RevenueData)?.payment_count || 0}</span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  // Calculate summary stats
  const total_invoiced = data?.reduce((sum, item) => sum + (item.invoiced || 0), 0) || 0;
  const total_collected = data?.reduce((sum, item) => sum + (item.collected || 0), 0) || 0;
  const total_outstanding = data?.reduce((sum, item) => sum + (item.outstanding || 0), 0) || 0;
  const collection_rate = total_invoiced > 0 
    ? Math.round((total_collected / total_invoiced) * 100) 
    : 0;

  // Format data for display
  const formatted_data: FormattedRevenueData[] = data?.map(item => ({
    ...item,
    period_display: format_period(item.period)
  })) || [];

  if (is_loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="h-96 flex items-center justify-center">
          <div className="animate-pulse text-gray-400">Loading chart data...</div>
        </div>
      </div>
    );
  }

  if (data?.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">Revenue Overview</h3>
            <p className="text-sm text-gray-500 mt-1">Track invoiced, collected, and outstanding amounts</p>
          </div>
        </div>
        <div className="h-80 flex flex-col items-center justify-center">
          <Activity size={48} className="text-gray-400 mb-4" />
          <p className="text-gray-600 font-medium">No revenue data available</p>
          <p className="text-sm text-gray-500 mt-1">
            Data will appear as invoices and payments are recorded
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">Revenue Overview</h3>
          <p className="text-sm text-gray-500 mt-1">
            Tracking {data?.length} period{data?.length !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => set_chart_type('area')}
            className={`p-2 rounded-lg transition-all ${
              chart_type === 'area'
                ? 'bg-forest-green text-white shadow-md'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            title="Area Chart"
          >
            <Layers size={18} />
          </button>
          <button
            onClick={() => set_chart_type('line')}
            className={`p-2 rounded-lg transition-all ${
              chart_type === 'line'
                ? 'bg-forest-green text-white shadow-md'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            title="Line Chart"
          >
            <TrendingUp size={18} />
          </button>
          <button
            onClick={() => set_chart_type('bar')}
            className={`p-2 rounded-lg transition-all ${
              chart_type === 'bar'
                ? 'bg-forest-green text-white shadow-md'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            title="Bar Chart"
          >
            <BarChart3 size={18} />
          </button>
        </div>
      </div>

      <div className="space-y-6">
        {/* Summary Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="p-4 bg-gradient-to-br from-forest-green/10 to-forest-green/20 rounded-lg border border-forest-green/30">
            <p className="text-xs text-forest-green font-medium mb-1 uppercase tracking-wide">Total Invoiced</p>
            <p className="text-2xl font-bold text-forest-green">
              {format_currency(total_invoiced)}
            </p>
            <p className="text-xs text-dark-forest mt-1">
              {format_tooltip_currency(total_invoiced)}
            </p>
          </div>
          <div className="p-4 bg-gradient-to-br from-success/10 to-success/20 rounded-lg border border-success/30">
            <p className="text-xs text-success font-medium mb-1 uppercase tracking-wide">Total Collected</p>
            <p className="text-2xl font-bold text-success">
              {format_currency(total_collected)}
            </p>
            <p className="text-xs text-green-700 mt-1">
              {format_tooltip_currency(total_collected)}
            </p>
          </div>
          <div className="p-4 bg-gradient-to-br from-warning/10 to-warning/20 rounded-lg border border-warning/30">
            <p className="text-xs text-warning font-medium mb-1 uppercase tracking-wide">Outstanding</p>
            <p className="text-2xl font-bold text-warning">
              {format_currency(total_outstanding)}
            </p>
            <p className="text-xs text-orange-700 mt-1">
              {format_tooltip_currency(total_outstanding)}
            </p>
          </div>
          <div className="p-4 bg-gradient-to-br from-lime-green/10 to-lime-green/20 rounded-lg border border-lime-green/30">
            <p className="text-xs text-forest-green font-medium mb-1 uppercase tracking-wide">Collection Rate</p>
            <p className="text-2xl font-bold text-forest-green">{collection_rate}%</p>
            <p className="text-xs text-dark-forest mt-1">
              {total_collected > 0 ? 'of total invoiced' : 'No collections yet'}
            </p>
          </div>
        </div>

        {/* Chart */}
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            {chart_type === 'area' ? (
              <AreaChart data={formatted_data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <defs>
                  <linearGradient id="colorInvoiced" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#1a3d2e" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#1a3d2e" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorCollected" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorOutstanding" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#f59e0b" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis 
                  dataKey="period_display" 
                  stroke="#6b7280"
                  style={{ fontSize: '12px' }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis 
                  stroke="#6b7280"
                  style={{ fontSize: '12px' }}
                  tickFormatter={format_currency}
                />
                <Tooltip content={custom_tooltip} />
                <Legend 
                  wrapperStyle={{ fontSize: '14px' }}
                  iconType="circle"
                />
                <Area
                  type="monotone"
                  dataKey="invoiced"
                  name="Invoiced"
                  stroke="#1a3d2e"
                  strokeWidth={2}
                  fill="url(#colorInvoiced)"
                />
                <Area
                  type="monotone"
                  dataKey="collected"
                  name="Collected"
                  stroke="#10b981"
                  strokeWidth={2}
                  fill="url(#colorCollected)"
                />
                <Area
                  type="monotone"
                  dataKey="outstanding"
                  name="Outstanding"
                  stroke="#f59e0b"
                  strokeWidth={2}
                  fill="url(#colorOutstanding)"
                />
              </AreaChart>
            ) : chart_type === 'line' ? (
              <LineChart data={formatted_data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis 
                  dataKey="period_display" 
                  stroke="#6b7280"
                  style={{ fontSize: '12px' }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis 
                  stroke="#6b7280"
                  style={{ fontSize: '12px' }}
                  tickFormatter={format_currency}
                />
                <Tooltip content={custom_tooltip} />
                <Legend 
                  wrapperStyle={{ fontSize: '14px' }}
                  iconType="circle"
                />
                <Line
                  type="monotone"
                  dataKey="invoiced"
                  name="Invoiced"
                  stroke="#1a3d2e"
                  strokeWidth={3}
                  dot={{ fill: '#1a3d2e', r: 5 }}
                  activeDot={{ r: 7 }}
                />
                <Line
                  type="monotone"
                  dataKey="collected"
                  name="Collected"
                  stroke="#10b981"
                  strokeWidth={3}
                  dot={{ fill: '#10b981', r: 5 }}
                  activeDot={{ r: 7 }}
                />
                <Line
                  type="monotone"
                  dataKey="outstanding"
                  name="Outstanding"
                  stroke="#f59e0b"
                  strokeWidth={3}
                  dot={{ fill: '#f59e0b', r: 5 }}
                  activeDot={{ r: 7 }}
                />
              </LineChart>
            ) : (
              <BarChart data={formatted_data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis 
                  dataKey="period_display" 
                  stroke="#6b7280"
                  style={{ fontSize: '12px' }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis 
                  stroke="#6b7280"
                  style={{ fontSize: '12px' }}
                  tickFormatter={format_currency}
                />
                <Tooltip content={custom_tooltip} />
                <Legend 
                  wrapperStyle={{ fontSize: '14px' }}
                  iconType="square"
                />
                <Bar dataKey="invoiced" name="Invoiced" fill="#1a3d2e" radius={[8, 8, 0, 0]} />
                <Bar dataKey="collected" name="Collected" fill="#10b981" radius={[8, 8, 0, 0]} />
                <Bar dataKey="outstanding" name="Outstanding" fill="#f59e0b" radius={[8, 8, 0, 0]} />
              </BarChart>
            )}
          </ResponsiveContainer>
        </div>

        {/* Legend Info */}
        <div className="flex flex-wrap gap-6 text-sm bg-off-white rounded-lg p-4 border border-light-gray">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-forest-green"></div>
            <span className="text-charcoal">
              <span className="font-semibold">Invoiced</span> - Total amount billed to clients
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-success"></div>
            <span className="text-charcoal">
              <span className="font-semibold">Collected</span> - Payments received from clients
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-warning"></div>
            <span className="text-charcoal">
              <span className="font-semibold">Outstanding</span> - Unpaid balance remaining
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export { RevenueChart };